import { useChannelTalk } from '@repo/channel-talk';
import { useUserEventLogger } from '@repo/tracking-service';
import { Button } from '@zenterprise-inc/ui';
import cn from 'clsx';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import s from './Landing.module.scss';

import { ScrollEventWrapper } from '@/components/common/ScrollEventWrapper';
import { LandingSectionProps } from '@/lib/constants/home';

interface Props {
  title: string;
  description: string;
  imgSrc: string;
  imgAlt?: string;
  buttonText: string;
  buttonLink?: string;
  onClick: () => void;
}

const HelpGuide = ({ sectionId, maxWidth }: LandingSectionProps) => {
  const { sendClickEvent } = useUserEventLogger();
  const { showMessenger } = useChannelTalk();

  return (
    <ScrollEventWrapper sectionId={sectionId}>
      <div className={'flex w-full justify-center'}>
        <div
          className={cn('app-side-padding grid w-full grid-cols-1 gap-6 pb-[9em] pt-[3em] sm:grid-cols-2', {
            ['max-w-screen-lg']: maxWidth === 'screen',
            ['max-w-[700px]']: maxWidth === '700',
            ['!grid-cols-1']: maxWidth === '700'
          })}
        >
          <HelpContent
            title={'더 자세한 내용이 궁금하신가요?'}
            description={'서비스 설명과 이용 방법, 더 자세한 FAQ까지. 이용 가이드에서 확인하세요.'}
            buttonText={'이용가이드 보기'}
            buttonLink={'https://help.bznav.com/hc/ko/sections/31995877295385'}
            imgSrc={'/images/tax-refund/fill-question-vt2-lg.svg'}
            onClick={() => {
              sendClickEvent('landing_bottom_helpcenter');
            }}
          />
          <HelpContent
            title={'해결할 수 없는 문제가 있으신가요?'}
            description={'고객센터 상담을 통해 정확한 답변, 자세한 설명을 들을 수 있어요.'}
            buttonText={'고객센터 문의'}
            imgSrc={'/images/tax-refund/fill-c-cs-lg.svg'}
            onClick={() => {
              sendClickEvent('landing_bottom_support-cx');

              showMessenger();
            }}
          />
        </div>
      </div>
    </ScrollEventWrapper>
  );
};

export default HelpGuide;

const HelpContent = ({ title, description, imgSrc, imgAlt, buttonText, buttonLink, onClick }: Props) => {
  const [iconSize, setIconSize] = useState(68);
  const widowResizeAction = () => {
    setIconSize(window.innerWidth < 768 ? 56 : 68);
  };

  useEffect(() => {
    window.addEventListener('resize', widowResizeAction);

    return () => window.removeEventListener('resize', widowResizeAction);
  }, []);

  return (
    <div className={'paragraph-3 mx-auto my-[2.5em] flex max-w-[320px] flex-col items-center'}>
      <img src={imgSrc} width={iconSize} height={iconSize} alt={imgAlt ?? ''} loading={'lazy'} />
      <div className={'paragraph-1 mb-[1em] mt-[0.75em] text-center'}>
        <h6 className={'paragraph-3 mb-[0.5em] font-semibold'}>{title}</h6>
        <p className={'paragraph-5'}>{description}</p>
      </div>
      {buttonLink ? (
        <Link href={buttonLink} target={'_blank'}>
          <Button.Text size={'large'} state={'primary'} className={cn(s.help_text_button, 'relative font-medium')} onClick={onClick}>
            {buttonText}
          </Button.Text>
        </Link>
      ) : (
        <Button.Text size={'large'} state={'primary'} className={cn(s.help_text_button, 'relative font-medium')} onClick={onClick}>
          {buttonText}
        </Button.Text>
      )}
    </div>
  );
};
